import { useQuery } from '@tanstack/react-query'
import { tenantKey } from '@/lib/queryClient'
import { useTenant } from '@/hooks/useTenant'
import { api } from '@/lib/api'

// Feature-flag names known to the frontend. Mirrors
// FEATURE_FLAGS in billing/services/plan-features.js. Adding a flag
// requires both ends; TS catches typos at the call site.
export type FeatureName =
  | 'payments_dashboard'
  | 'payments_refunds'
  | 'payments_connect_express'
  | 'payments_managed'
  | 'ar_invoicing'
  | 'ap_bills'
  | 'recurring_invoices'
  | 'approvals_engine'
  | 'workflows_engine'
  | 'ai_agents'
  | 'ai_analytics'
  | 'bank_feeds'

type FeatureMap = Record<FeatureName, boolean>

// Single fetch on first call, cached for the session. Bokapsys staff
// flipping a flag in the platform-admin tab takes effect after the
// tenant reloads (or after the 30 s server-side cache window expires
// AND the React Query staleTime expires).
export function useFeatures() {
  const tenantId = useTenant()
  const q = useQuery({
    queryKey: tenantKey(tenantId, 'billing', 'features'),
    queryFn: () => api.get<{ features: FeatureMap }>('/api/billing/features'),
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  })
  const features = q.data?.features

  return {
    features,
    isLoading: q.isLoading,
    /**
     * True if the named feature is enabled for this tenant. Defaults
     * to `false` while loading and on error - fail closed so a network
     * blip doesn't accidentally surface a gated feature.
     */
    hasFeature(name: FeatureName): boolean {
      if (!features) return false
      return !!features[name]
    },
  }
}
